import { useState } from "react";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import api from "@/lib/api";
import { TRUCK_TYPES, MATERIAL_TYPES } from "@/lib/constants";
import { PasteAutoFill } from "@/components/PasteAutoFill";

export default function PostLoadDialog({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    origin: "", destination: "", material_type: "", weight: "",
    temp_min: "-18", temp_max: "4", truck_type: "", pickup_date: "", notes: "",
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const applyFilled = (fields) => setForm((f) => ({ ...f, ...Object.fromEntries(Object.entries(fields).filter(([, v]) => v !== "")) }));

  const postAll = async (items) => {
    let ok = 0, skip = 0;
    for (const it of items) {
      if (!it.origin || !it.destination || !it.material_type || !it.truck_type || !it.weight || !it.pickup_date) { skip++; continue; }
      try {
        await api.post("/loads", {
          origin: it.origin, destination: it.destination, material_type: it.material_type,
          weight: parseFloat(it.weight),
          temp_min: it.temp_min !== "" ? parseFloat(it.temp_min) : -18,
          temp_max: it.temp_max !== "" ? parseFloat(it.temp_max) : 4,
          truck_type: it.truck_type, pickup_date: it.pickup_date, notes: it.notes || "",
        });
        ok++;
      } catch (e) { skip++; }
    }
    if (ok) toast.success(`Posted ${ok} load${ok > 1 ? "s" : ""}${skip ? ` · ${skip} skipped (missing details)` : ""}`);
    else { toast.error("Couldn't post — entries were missing required details."); throw new Error("none posted"); }
    setOpen(false);
    onCreated?.();
  };

  const submit = async () => {
    if (!form.origin || !form.destination || !form.material_type || !form.truck_type || !form.weight || !form.pickup_date) {
      toast.error("Please fill origin, destination, cargo, weight, truck type and date.");
      return;
    }
    setSaving(true);
    try {
      await api.post("/loads", {
        ...form,
        weight: parseFloat(form.weight),
        temp_min: parseFloat(form.temp_min),
        temp_max: parseFloat(form.temp_max),
      });
      toast.success("Load posted to the board");
      setOpen(false);
      setForm({ origin: "", destination: "", material_type: "", weight: "", temp_min: "-18", temp_max: "4", truck_type: "", pickup_date: "", notes: "" });
      onCreated?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not post load");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="open-post-load-btn" className="rounded-full cta-accent font-semibold">
          <Plus className="w-4 h-4 mr-1.5" /> Post a Load
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-card border-border max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">Post a Load</DialogTitle>
          <DialogDescription>Share your freight so transporters with reefer trucks can reach you.</DialogDescription>
        </DialogHeader>
        <div className="pt-1"><PasteAutoFill kind="load" onFilled={applyFilled} onPostAll={postAll} /></div>
        <div className="grid grid-cols-2 gap-4 py-2">
          <div className="space-y-1.5">
            <Label>Origin</Label>
            <Input data-testid="load-origin-input" value={form.origin} onChange={(e) => set("origin", e.target.value)} placeholder="e.g. Pune" />
          </div>
          <div className="space-y-1.5">
            <Label>Destination</Label>
            <Input data-testid="load-destination-input" value={form.destination} onChange={(e) => set("destination", e.target.value)} placeholder="e.g. Delhi" />
          </div>
          <div className="space-y-1.5 col-span-2">
            <Label>Material / Cargo</Label>
            <Select value={form.material_type} onValueChange={(v) => set("material_type", v)}>
              <SelectTrigger data-testid="load-material-select"><SelectValue placeholder="Select cargo type" /></SelectTrigger>
              <SelectContent>{MATERIAL_TYPES.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Weight (tonnes)</Label>
            <Input data-testid="load-weight-input" type="number" value={form.weight} onChange={(e) => set("weight", e.target.value)} placeholder="e.g. 9" />
          </div>
          <div className="space-y-1.5">
            <Label>Truck type needed</Label>
            <Select value={form.truck_type} onValueChange={(v) => set("truck_type", v)}>
              <SelectTrigger data-testid="load-trucktype-select"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>{TRUCK_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Temp min (°C)</Label>
            <Input data-testid="load-tempmin-input" type="number" value={form.temp_min} onChange={(e) => set("temp_min", e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label>Temp max (°C)</Label>
            <Input data-testid="load-tempmax-input" type="number" value={form.temp_max} onChange={(e) => set("temp_max", e.target.value)} />
          </div>
          <div className="space-y-1.5 col-span-2">
            <Label>Pickup date</Label>
            <Input data-testid="load-date-input" type="date" value={form.pickup_date} onChange={(e) => set("pickup_date", e.target.value)} />
          </div>
          <div className="space-y-1.5 col-span-2">
            <Label>Notes (optional)</Label>
            <Textarea data-testid="load-notes-input" value={form.notes} onChange={(e) => set("notes", e.target.value)} placeholder="Rate expectation, loading details…" />
          </div>
        </div>
        <p className="text-xs text-muted-foreground px-1">Your load stays live until 1 day after the pickup date. Mark it placed once a vehicle is assigned.</p>
        <DialogFooter>
          <Button data-testid="submit-load-btn" onClick={submit} disabled={saving} className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90 w-full">
            {saving ? "Posting…" : "Post Load"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
